import React, { useEffect, useState } from 'react'

import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    TextField,
    Typography,
    useTheme,
} from '@material-ui/core'
import {
    KeyboardDatePicker,
    KeyboardDateTimePicker,
} from '@material-ui/pickers'
import { toast } from 'react-toastify'

import { Container } from '../../components'
import { Concession, RenewalOffer } from '../../models/Concession'
import { useConcessions } from '../../hooks/useConcessions'
import { useRenewalOffers } from '../../hooks/useRenewalOffers'
import { toYYYYMMDD } from '../../helpers/date'
import { useConcessionStyles } from '../Concessions/styles'

interface Props {
    offer?: RenewalOffer
    handleClose: () => void
}

export const EditOfferModal: React.FC<Props> = ({ offer, handleClose }) => {
    const theme = useTheme()
    const classes = useConcessionStyles()
    const { concessions } = useConcessions()
    const { updateOffer } = useRenewalOffers()

    const [proposedRent, setProposedRent] = useState('')
    const [proposedTermMonths, setProposedTermMonths] = useState('')
    const [proposedStartDate, setProposedStartDate] = useState('')
    const [concessionIds, setConcessionIds] = useState<number[]>([])
    const [expiresAt, setExpiresAt] = useState<Date | null>(null)

    useEffect(() => {
        if (offer) {
            setProposedRent(offer.proposed_rent)
            setProposedTermMonths(String(offer.proposed_term_months))
            setProposedStartDate(offer.proposed_start_date)
            setConcessionIds(offer.concessions ?? [])
            setExpiresAt(offer.expires_at ? new Date(offer.expires_at) : null)
        }
    }, [offer])

    const activeConcessions = concessions.filter((c: Concession) => c.is_active)

    const handleSave = () => {
        const rent = parseFloat(proposedRent)
        if (!proposedRent || Number.isNaN(rent) || rent <= 0) {
            toast.error('Proposed rent must be greater than 0.')
            return
        }
        const term = parseInt(proposedTermMonths, 10)
        if (
            !proposedTermMonths ||
            Number.isNaN(term) ||
            !Number.isInteger(term) ||
            term < 1
        ) {
            toast.error('Term must be at least 1 month.')
            return
        }
        if (!proposedStartDate) {
            toast.error('Start date is required.')
            return
        }
        if (!offer) return

        updateOffer.mutate(
            {
                id: offer.id,
                body: {
                    proposed_rent: rent.toFixed(2),
                    proposed_term_months: term,
                    proposed_start_date: proposedStartDate,
                    concessions: concessionIds,
                    expires_at: expiresAt ? expiresAt.toISOString() : null,
                },
            },
            { onSuccess: () => handleClose() },
        )
    }

    const open = !!offer && offer.status === 'draft'

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Edit Draft — {offer?.tenant_name ?? ''}{' '}
                <Typography
                    variant="body2"
                    color="textSecondary"
                    component="span"
                >
                    ({offer?.area_name})
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Container
                    direction="column"
                    style={{
                        gap: theme.spacing(2),
                        paddingTop: theme.spacing(1),
                    }}
                >
                    <div className={classes.row}>
                        <TextField
                            label="Monthly rent ($)"
                            variant="outlined"
                            size="small"
                            type="number"
                            value={proposedRent}
                            onChange={(e) => setProposedRent(e.target.value)}
                            inputProps={{ min: 0, step: '0.01' }}
                            required
                            style={{ flex: 1 }}
                        />
                        <TextField
                            label="Term (months)"
                            variant="outlined"
                            size="small"
                            type="number"
                            value={proposedTermMonths}
                            onChange={(e) =>
                                setProposedTermMonths(e.target.value)
                            }
                            inputProps={{ min: 1, step: 1 }}
                            required
                            style={{ flex: 1 }}
                        />
                    </div>
                    <div className={classes.row}>
                        <KeyboardDatePicker
                            label="Start date"
                            format="MM/dd/yyyy"
                            inputVariant="outlined"
                            size="small"
                            value={
                                proposedStartDate
                                    ? new Date(proposedStartDate + 'T00:00:00')
                                    : null
                            }
                            onChange={(date) =>
                                setProposedStartDate(
                                    date ? toYYYYMMDD(date) : '',
                                )
                            }
                            style={{ flex: 1 }}
                        />
                        <KeyboardDateTimePicker
                            label="Expires at (optional)"
                            format="MM/dd/yyyy hh:mm a"
                            inputVariant="outlined"
                            size="small"
                            value={expiresAt}
                            onChange={(date) => setExpiresAt(date)}
                            clearable
                            style={{ flex: 1 }}
                        />
                    </div>
                    <FormControl
                        variant="outlined"
                        size="small"
                        style={{ width: '100%' }}
                    >
                        <InputLabel
                            id="edit-concession-multi-label"
                            shrink={concessionIds.length > 0}
                        >
                            Concessions
                        </InputLabel>
                        <Select
                            labelId="edit-concession-multi-label"
                            multiple
                            value={concessionIds}
                            onChange={(e) =>
                                setConcessionIds(e.target.value as number[])
                            }
                            label={
                                concessionIds.length > 0
                                    ? 'Concessions'
                                    : undefined
                            }
                            renderValue={(selected) => {
                                const ids = selected as number[]
                                if (ids.length === 0) return <></>
                                return (
                                    <Box
                                        display="flex"
                                        flexWrap="nowrap"
                                        alignItems="center"
                                        style={{ gap: 4, overflow: 'hidden' }}
                                    >
                                        {ids.map((id) => {
                                            const c = activeConcessions.find(
                                                (x) => x.id === id,
                                            )
                                            return (
                                                <Chip
                                                    key={id}
                                                    label={c?.name ?? id}
                                                    size="small"
                                                    style={{
                                                        height: 20,
                                                        fontSize: '0.675rem',
                                                        fontWeight: 600,
                                                        backgroundColor:
                                                            '#d1fae5',
                                                        color: '#047857',
                                                        borderRadius: 6,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                            )
                                        })}
                                    </Box>
                                )
                            }}
                            style={{ maxHeight: 40 }}
                            MenuProps={{
                                PaperProps: {
                                    style: { maxHeight: 300, marginTop: 4 },
                                },
                                anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                },
                                transformOrigin: {
                                    vertical: 'top',
                                    horizontal: 'left',
                                },
                                getContentAnchorEl: null,
                            }}
                        >
                            {activeConcessions.map((c) => (
                                <MenuItem
                                    key={c.id}
                                    value={c.id}
                                    style={{ paddingTop: 6, paddingBottom: 6 }}
                                >
                                    <Checkbox
                                        checked={concessionIds.includes(c.id)}
                                        size="small"
                                        style={{
                                            color: '#047857',
                                            padding: '0 8px 0 0',
                                        }}
                                    />
                                    <ListItemText
                                        primary={c.name}
                                        primaryTypographyProps={{
                                            variant: 'body2',
                                            style: { fontWeight: 500 },
                                        }}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Container>
            </DialogContent>

            <DialogActions style={{ padding: theme.spacing(2, 3) }}>
                <Button
                    onClick={handleClose}
                    disabled={updateOffer.isLoading}
                    className={classes.cancelButton}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={updateOffer.isLoading}
                    variant="contained"
                    className={classes.saveButton}
                >
                    {updateOffer.isLoading ? 'Saving...' : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
